import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox } from 'antd'
import { isEmpty } from 'ramda'
import $t from '../../../i18n'
import MaterialInput from '../../common/MaterialInput'
import Actions from '../../../actions'
import { COUNTRY_CODES } from '../../../consts'
import { event, eventTypes } from '../../../consts/eventConsts'
import { formatClientSideError } from '../../../utils/eventUtils'
import Page from '../../common/Page/Page'
import PageCard from '../../common/PageCard'
import EditableItem from '../../common/EditableItem'
import { getFamilyEmergencyContacts } from '../../../utils/familyUtils'
import { filterCanEditPersonalData } from '../../../utils/familyPermissionsUtils'

import './EmergencyContact.css'

const getValidationErrors = ({ phoneNumber, contactName }) => {
  const result = {}
  const numericRegex = /^\d{10}$/

  if (!phoneNumber.match(numericRegex)) {
    result.phoneNumber = $t('errors.validation.invalidPhone')
  }

  if (!phoneNumber) {
    result.phoneNumber = $t('errors.validation.missing')
  }

  if (!contactName) {
    result.contactName = $t('errors.validation.missing')
  }

  return (!isEmpty(result) && result) || null
}

const EmergencyContact = ({ onNext }) => {
  const dispatch = useDispatch()

  // in case we have on next function, we are inside the wizard
  // we want to know that in order to render the emergency screen outside the pageCard
  const isInsideWizard = !!onNext

  const userDetails = useSelector(state => state.profile.userDetails)
  const [inEditMode, setInEditMode] = useState(
    !!(isInsideWizard && !userDetails?.emergencyContact?.contactName)
  )
  const selectedMember = useSelector(state => state.account?.selectedMember)
  const [memberToEdit, setMemberToEdit] = useState(null)
  const anotherPatient = selectedMember ? selectedMember?.id : memberToEdit
  const currentPatient = userDetails?.id
  const activeMembers = useSelector(state => state.account?.activeMembers)
  const familyEmergencyContacts = getFamilyEmergencyContacts(
    activeMembers,
    userDetails
  )
  const emergencyContactsByMember = selectedMember?.id
    ? familyEmergencyContacts?.filter(
        contact =>
          contact?.memberId.toString() === selectedMember?.id.toString()
      )
    : familyEmergencyContacts

  const familyCanEdit = activeMembers?.some(member =>
    filterCanEditPersonalData(member)
  )
  const [phoneNumber, setPhoneNumber] = useState({
    short: 'US',
    code: COUNTRY_CODES[0],
    phone: '',
  })
  const [contactName, setContactName] = useState('')
  const [shouldValidate, setShouldValidate] = useState(false)
  const [useForFamily, setUseForFamily] = useState(false)

  const errors =
    shouldValidate &&
    getValidationErrors({ phoneNumber: phoneNumber?.phone, contactName })

  const getFamilyNames = () => {
    const names = []
    familyEmergencyContacts?.forEach(member => {
      if (member?.memberId === currentPatient) {
        names.push($t('dialogs.emergencyContact.myself'))
      } else {
        var currMember = activeMembers.find(
          activeMember => activeMember?.id === member?.memberId
        )
        if (filterCanEditPersonalData(currMember)) {
          names.push(currMember?.firstName)
        }
      }
    })
    return names.join(', ')
  }

  const onBack = () => {
    if (inEditMode) {
      setInEditMode(false)
      setContactName('')
      onResetPhoneNumber(phoneNumber)
    } else {
      dispatch(Actions.createAction(Actions.NAVIGATED_BACK))
    }
  }

  const onEdit = contact => {
    setInEditMode(true)
    setContactName(contact?.contactName)
    setMemberToEdit(contact?.memberId)
    onResetPhoneNumber(contact?.phoneNumber)
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        element: 'Edit emergency contact',
      })
    )
  }

  const onResetPhoneNumber = currPhoneNumber => {
    for (const countryCode of COUNTRY_CODES) {
      if (
        currPhoneNumber &&
        currPhoneNumber.toString().startsWith(`+${countryCode}`)
      ) {
        const phone = currPhoneNumber.toString().replace(`+${countryCode}`, '')
        const code = countryCode.toString()
        setPhoneNumber(prev => ({ ...prev, phone, code }))
        break
      }
    }
  }

  const onOk = payload => {
    if (useForFamily) {
      familyEmergencyContacts?.forEach(member => {
        var currMember = activeMembers?.find(
          activeMember => activeMember?.id === member?.memberId
        )
        // update only for members that can edit personal data or for the current user
        if (
          (currMember && filterCanEditPersonalData(currMember)) ||
          member?.memberId === currentPatient
        ) {
          dispatch(
            Actions.createAction(Actions.UPDATE_EMERGENCY_CONTACT_REQUESTED, {
              ...payload,
              patientId: member?.memberId,
            })
          )
        }
      })
    } else {
      dispatch(
        Actions.createAction(Actions.UPDATE_EMERGENCY_CONTACT_REQUESTED, {
          ...payload,
          patientId: anotherPatient ? anotherPatient : currentPatient,
        })
      )
    }
  }

  const onSubmit = () => {
    // remove '-' and '.' chars from number, is exists. remove spaces
    const phoneWithoutChars = phoneNumber?.phone
      ? phoneNumber?.phone
          .toString()
          .replace(new RegExp('[-.]', 'g'), '')
          .replace(/\s/g, '')
      : ''

    const payload = {
      phoneNumber: '+' + phoneNumber?.code.toString() + phoneWithoutChars,
      contactName,
    }

    const validationErrors = getValidationErrors({
      phoneNumber: phoneWithoutChars,
      contactName,
    })

    if (validationErrors) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: payload,
          element: 'Emergency Contact',
          error: formatClientSideError(getValidationErrors(payload)),
        })
      )
      return
    }

    if (onOk) {
      onOk(payload)
      setInEditMode(false)
    }

    // on next comes from wizard component
    // we can use this component without the wizard and and 'onnext' will be undefined
    if (onNext) {
      onNext()
    }
  }

  const renderContent = () => {
    return (
      <>
        <div className="input-container">
          <MaterialInput
            className="name-input"
            value={contactName}
            errorText={errors?.contactName}
            onChange={e => setContactName(e.target.value)}
            defaultValue={contactName}
            disabled={!inEditMode}
            label={$t('dialogs.emergencyContact.name')}
          />
          <MaterialInput
            className="phone-input"
            onChange={e => setPhoneNumber(e)}
            disabled={!inEditMode}
            value={phoneNumber}
            errorText={errors?.phoneNumber}
            label={$t('dialogs.emergencyContact.phone')}
            type="phone"
          />
          {familyCanEdit && (
            <Checkbox
              checked={useForFamily}
              onChange={() => setUseForFamily(!useForFamily)}
            >
              {$t('dialogs.emergencyContact.useForFamily', {
                names: getFamilyNames(),
              })}
            </Checkbox>
          )}
          <div className="page-footer">
            <Button
              key="1"
              className="dialog-action trackable"
              data-name="save"
              type="primary"
              onClick={onSubmit}
            >
              {$t('dialogs.emergencyContact.saveChanges')}
            </Button>
          </div>
        </div>
      </>
    )
  }

  const getEmergencyPage = () => {
    return (
      <Page
        displayManageFamily={!inEditMode && !isInsideWizard}
        hideAlertBanner={true}
        title={$t('dialogs.emergencyContact.title')}
        className="emergency-contact-page"
        subtitle={$t('dialogs.emergencyContact.subtitle')}
      >
        <div className="emergency-contact-page-wrapper">
          {!inEditMode &&
            emergencyContactsByMember.map((contact, index) => {
              return (
                <EditableItem
                  key={index}
                  dataName={'emergency-contact ' + contact?.contactName}
                  onEdit={() => onEdit(contact)}
                  disableEdit={!contact?.canEdit}
                  membersIds={[contact?.memberId]}
                >
                  {contact?.contactName ? (
                    <div className="contact-name">
                      <div className="title">{contact?.contactName}</div>
                      <div className="description">{contact?.phoneNumber}</div>
                    </div>
                  ) : (
                    <div className="title">
                      {$t('dialogs.emergencyContact.noDetails')}
                    </div>
                  )}
                </EditableItem>
              )
            })}
        </div>
        {inEditMode && renderContent()}
      </Page>
    )
  }

  return isInsideWizard ? (
    getEmergencyPage()
  ) : (
    <PageCard enableBack={true} onBack={onBack}>
      {getEmergencyPage()}
    </PageCard>
  )
}

export default EmergencyContact
